import {
	Component,
	ViewEncapsulation,
	Input,
	Output,
	EventEmitter,
	ViewChild,
	ElementRef,
	ChangeDetectionStrategy
} from "@angular/core";
import { makeDateUtils } from "../utils/DateUtils";

import * as _moment from "moment";

@Component({
	selector: "rbc-datepicker-month",
	templateUrl: "./datepicker-month.component.html",
	styles: [],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatepickerMonthComponent {
	@Input() id;
	@Input() showMonthView: Boolean;
	@Input() selectedDate: Date;
	@Input() pageDate: Date;
	@Input() pageTimestamp: Number;
	@Input() allowedToShowView;
	@Input() disabledDates: Object;
	@Input() calendarClass: any;
	@Input() calendarStyle: Object;
	@Input() translation: Object;
	@Input() isRtl: Boolean;
	@Input() useUtc: Boolean;

	@Output() selectMonthEmit = new EventEmitter();
	@Output() yearChanged = new EventEmitter();
	@Output() calendarShowYear = new EventEmitter();

	@ViewChild("monthContainer", { static: false }) monthContainer: ElementRef;

	constructedDateUtils = makeDateUtils(this.useUtc);
	utils = this.constructedDateUtils;

	months() {
		const d = this.pageDate;
		let months = [];
		// set up a new date object to the beginning of the current 'page'
		let dObj = this.useUtc
			? new Date(Date.UTC(d.getUTCFullYear(), 0, d.getUTCDate()))
			: new Date(
				d.getFullYear(),
				0,
				d.getDate(),
				d.getHours(),
				d.getMinutes()
			);
		for (let i = 0; i < 12; i++) {
			months.push({
				month: this.utils.getMonthName(i, this.translation["months"]),
				timestamp: dObj.getTime(),
				isSelected: this.isSelectedMonth(dObj),
				isDisabled: this.isDisabledMonth(dObj)
			});
			this.utils.setMonth(dObj, this.utils.getMonth(dObj) + 1);
		}
		return months;
	}

	/**
	 * Get year name on current page.
	 */
	pageYearName() {
		const yearSuffix = this.translation["yearSuffix"];
		return `${this.utils.getFullYear(this.pageDate)}${yearSuffix}`;
	}

	/**
	 * Is the left hand navigation disabled
	 */
	isLeftNavDisabled() {
		return this.isRtl
			? this.isNextYearDisabled(this.pageTimestamp)
			: this.isPreviousYearDisabled(this.pageTimestamp);
	}

	/**
	 * Is the right hand navigation disabled
	 */
	isRightNavDisabled() {
		return this.isRtl
			? this.isPreviousYearDisabled(this.pageTimestamp)
			: this.isNextYearDisabled(this.pageTimestamp);
	}

	/**
	 * Emits a selectMonth event
	 */
	selectMonth(month) {
		if (month.isDisabled) {
			return false;
		}
		this.selectMonthEmit.emit(month);
	}

	/**
	 * Changes the year up or down
	 */
	changeYear(incrementBy) {
		let date = this.pageDate;
		this.utils.setFullYear(
			date,
			this.utils.getFullYear(date) + incrementBy
		);
		this.yearChanged.emit(date);
	}

	/**
	 * Decrements the year
	 */
	previousYear() {
		if (!this.isPreviousYearDisabled()) {
			this.changeYear(-1);
		}
	}

	/**
	 * Checks if the previous year is disabled or not
	 */
	isPreviousYearDisabled(timestamp?) {
		if (!this.disabledDates || !this.disabledDates["to"]) {
			return false;
		}
		return (
			this.utils.getFullYear(this.disabledDates["to"]) >=
			this.utils.getFullYear(this.pageDate)
		);
	}

	/**
	 * Increments the year
	 */
	nextYear() {
		if (!this.isNextYearDisabled()) {
			this.changeYear(1);
		}
	}

	/**
	 * Checks if the next year is disabled or not
	 */
	isNextYearDisabled(timestamp?) {
		if (!this.disabledDates || !this.disabledDates["from"]) {
			return false;
		}
		return (
			this.utils.getFullYear(this.disabledDates["from"]) <=
			this.utils.getFullYear(this.pageDate)
		);
	}

	/**
	 * Emits an event that shows the year calendar
	 */
	showYearCalendar() {
		this.calendarShowYear.emit();
	}

	/**
	 * Whether the selected date is in this month
	 */
	isSelectedMonth(date) {
		return (
			this.selectedDate &&
			this.utils.getFullYear(this.selectedDate) ===
			this.utils.getFullYear(date) &&
			this.utils.getMonth(this.selectedDate) === this.utils.getMonth(date)
		);
	}

	/**
	 * Whether a month is disabled
	 */
	isDisabledMonth(date) {
		let disabledDates = false;

		if (typeof this.disabledDates === "undefined") {
			return false;
		}

		if (
			typeof this.disabledDates["to"] !== "undefined" &&
			this.disabledDates["to"]
		) {
			if (
				(this.utils.getMonth(date) <
					this.utils.getMonth(this.disabledDates["to"]) &&
					this.utils.getFullYear(date) <=
					this.utils.getFullYear(this.disabledDates["to"])) ||
				this.utils.getFullYear(date) <
				this.utils.getFullYear(this.disabledDates["to"])
			) {
				disabledDates = true;
			}
		}
		if (
			typeof this.disabledDates["from"] !== "undefined" &&
			this.disabledDates["from"]
		) {
			if (
				(this.utils.getMonth(date) >
					this.utils.getMonth(this.disabledDates["from"]) &&
					this.utils.getFullYear(date) >=
					this.utils.getFullYear(this.disabledDates["from"])) ||
				this.utils.getFullYear(date) >
				this.utils.getFullYear(this.disabledDates["from"])
			) {
				disabledDates = true;
			}
		}

		if (
			typeof this.disabledDates["customPredictor"] === "function" &&
			this.disabledDates["customPredictor(date)"]
		) {
			disabledDates = true;
		}
		return disabledDates;
	}

	prevent(event) {
		event.stopPropagation();
	}
}
